/**
 * Multi-tabs project manager — slicer-style multi-project workflow.
 * Each tab holds its own snapshot of the persisted state slice.
 * Switching tabs swaps the live state with the tab's snapshot.
 */
import useEditorStore from './useEditorStore';

const PERSISTED_KEYS = [
  'segments', 'activeSegmentId', 'structureType', 'dimensions', 'inputMode',
  'fontIndex', 'partsVisibility', 'background', 'clips', 'standoffs',
  'ledOn', 'ledDensity', 'light', 'pricing', 'printer', 'printSettings',
  'socleRim', 'mountingHoles', 'cableHole', 'meta',
];

function uid() { return Math.random().toString(36).slice(2, 9); }

export function snapshotFromState(state) {
  const snap = {};
  PERSISTED_KEYS.forEach((k) => { if (state[k] !== undefined) snap[k] = state[k]; });
  return JSON.parse(JSON.stringify(snap));
}

export function applySnapshot(snap) {
  const patch = {};
  PERSISTED_KEYS.forEach((k) => {
    if (snap[k] !== undefined) patch[k] = JSON.parse(JSON.stringify(snap[k]));
  });
  useEditorStore.setState(patch);
}

/* ----- Tabs API ----- */
export function getTabs() { return useEditorStore.getState().tabs; }
export function getActiveTab() {
  const s = useEditorStore.getState();
  return s.tabs.find((t) => t.tabId === s.activeTabId) || null;
}

export function ensureInitialTab() {
  const s = useEditorStore.getState();
  if (s.tabs.length === 0) {
    const tabId = uid();
    const tab = {
      tabId,
      projectId: null,
      meta: { ...s.meta },
      snapshot: snapshotFromState(s),
      dirty: false,
    };
    useEditorStore.setState({ tabs: [tab], activeTabId: tabId });
  }
}

export function newTab(name = 'Nouveau projet') {
  const s = useEditorStore.getState();
  // Save current tab snapshot before switching
  const tabs = s.tabs.map((t) => t.tabId === s.activeTabId
    ? { ...t, meta: { ...s.meta }, snapshot: snapshotFromState(s) }
    : t,
  );
  // Build a fresh blank snapshot using DEFAULT_PROJECT + fresh segments
  // We rely on a "freshState" by reading the initial state of a hidden helper.
  // Simpler: clone current and just reset meta + use 1 default segment.
  const blank = snapshotFromState(s);
  blank.meta = { name, client: '', installAddress: '', contactEmail: '', contactPhone: '', deadlineDate: '', reference: '', notes: '' };
  // Reset segments to default
  // eslint-disable-next-line no-undef
  blank.segments = JSON.parse(JSON.stringify(s.segments.map((seg, i) => i === 0 ? { ...seg, text: 'Texte' } : null).filter(Boolean)));
  if (blank.segments.length === 0) blank.segments = [{
    id: uid(), text: 'Texte',
    materials: { walls: 'petg', back: 'petg', front: 'pmma' },
    colors: { walls: '#ec1d8c', back: '#161616', front: '#f5f5f0' },
    letters: [], detached: false, gapAfterMm: 30,
  }];
  const tabId = uid();
  const newT = { tabId, projectId: null, meta: blank.meta, snapshot: blank, dirty: false };
  useEditorStore.setState({ tabs: [...tabs, newT], activeTabId: tabId });
  applySnapshot(blank);
}

export function openExistingProjectInTab(project) {
  const s = useEditorStore.getState();
  // If a tab already shows this project, just switch to it
  const existing = s.tabs.find((t) => t.projectId === project.project_id);
  if (existing) {
    switchTab(existing.tabId);
    return;
  }
  // Save current tab
  const tabs = s.tabs.map((t) => t.tabId === s.activeTabId
    ? { ...t, meta: { ...s.meta }, snapshot: snapshotFromState(s) }
    : t,
  );
  const tabId = uid();
  const snap = project.state || {};
  // Ensure meta exists in snapshot
  if (!snap.meta) snap.meta = { name: project.name || 'Sans titre' };
  const tab = {
    tabId,
    projectId: project.project_id,
    meta: snap.meta,
    snapshot: snap,
    dirty: false,
  };
  useEditorStore.setState({ tabs: [...tabs, tab], activeTabId: tabId });
  applySnapshot(snap);
}

export function switchTab(tabId) {
  const s = useEditorStore.getState();
  if (s.activeTabId === tabId) return;
  // Save current tab's snapshot
  const tabs = s.tabs.map((t) => t.tabId === s.activeTabId
    ? { ...t, meta: { ...s.meta }, snapshot: snapshotFromState(s) }
    : t,
  );
  const next = tabs.find((t) => t.tabId === tabId);
  if (!next) return;
  useEditorStore.setState({ tabs, activeTabId: tabId });
  applySnapshot(next.snapshot);
}

export function closeTab(tabId) {
  const s = useEditorStore.getState();
  if (s.tabs.length <= 1) return; // never close the last tab
  const remaining = s.tabs.filter((t) => t.tabId !== tabId);
  let activeTabId = s.activeTabId;
  if (s.activeTabId === tabId) {
    // Switch to neighbor
    activeTabId = remaining[remaining.length - 1].tabId;
    applySnapshot(remaining[remaining.length - 1].snapshot);
  }
  useEditorStore.setState({ tabs: remaining, activeTabId });
}

export function updateActiveTabMeta(meta) {
  const s = useEditorStore.getState();
  const tabs = s.tabs.map((t) => t.tabId === s.activeTabId ? { ...t, meta: { ...t.meta, ...meta }, dirty: true } : t);
  useEditorStore.setState({ tabs });
}

export function setActiveTabProjectId(projectId, savedMeta) {
  const s = useEditorStore.getState();
  const tabs = s.tabs.map((t) => t.tabId === s.activeTabId
    ? { ...t, projectId, meta: savedMeta || t.meta, dirty: false, snapshot: snapshotFromState(s) }
    : t,
  );
  useEditorStore.setState({ tabs });
}

export function markActiveTabDirty() {
  const s = useEditorStore.getState();
  const tabs = s.tabs.map((t) => t.tabId === s.activeTabId ? { ...t, dirty: true } : t);
  useEditorStore.setState({ tabs });
}
